import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Wand2, Check, X, Loader2, RotateCcw } from "lucide-react";

export default function AdminAIQuestions() {
  const [jobs, setJobs] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [selected, setSelected] = useState(null); // current draft
  const [gen, setGen] = useState({ job_id:"", count:10, difficulty:"mixed" });
  const [loading, setLoading] = useState(false);

  const loadJobs = () => api.get("/admin/jobs").then(r => setJobs(r.data));
  const loadDrafts = () => api.get("/admin/ai-questions/drafts").then(r => setDrafts(r.data));
  useEffect(() => { loadJobs(); loadDrafts(); }, []);

  const generate = async () => {
    if (!gen.job_id) { toast.error("Please select a job"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/admin/ai-questions/generate", {
        job_id: gen.job_id, count: gen.count, difficulty: gen.difficulty, question_types: ["MCQ"]
      });
      toast.success(`Generated ${data.count_generated} questions`);
      setSelected(data);
      loadDrafts();
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setLoading(false); }
  };

  const updateQ = (i, field, val) => {
    const qs = [...selected.questions]; qs[i] = { ...qs[i], [field]: val };
    setSelected({ ...selected, questions: qs });
  };
  const updateOpt = (i, oi, val) => {
    const qs = [...selected.questions]; qs[i].options[oi] = val;
    setSelected({ ...selected, questions: qs });
  };
  const removeQ = (i) => setSelected({ ...selected, questions: selected.questions.filter((_, idx) => idx !== i) });
  const saveDraft = async () => {
    try {
      await api.put(`/admin/ai-questions/drafts/${selected.id}`, { questions: selected.questions });
      toast.success("Draft saved");
    } catch (e) { toast.error("Save failed"); }
  };
  const approve = async () => {
    try {
      await saveDraft();
      const { data } = await api.post(`/admin/ai-questions/drafts/${selected.id}/approve`);
      toast.success(`Approved & saved ${data.saved_count} questions to bank`);
      setSelected(null); loadDrafts();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
  };
  const openDraft = async (did) => {
    const { data } = await api.get(`/admin/ai-questions/drafts/${did}`);
    setSelected(data);
  };

  return (
    <AdminLayout>
      <h1 className="text-2xl font-bold text-white">AI Question Generator</h1>
      <p className="text-slate-400 text-sm mt-1">Generate job-specific assessment questions with GPT 5.6 Terra. Review, edit, and approve before saving to the Question Bank.</p>

      <Card className="mt-6 p-6 bg-slate-900 border-slate-800">
        <div className="grid md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-2">
            <Label className="text-slate-300">Job</Label>
            <Select value={gen.job_id} onValueChange={v=>setGen({...gen, job_id:v})}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="aiq-job"><SelectValue placeholder="Select a job"/></SelectTrigger>
              <SelectContent>{jobs.map(j=><SelectItem key={j.id} value={j.id}>{j.title} · {j.experience_category}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300">Count</Label>
            <Input type="number" min={1} max={30} value={gen.count} onChange={e=>setGen({...gen, count: parseInt(e.target.value||"10",10)})} className="bg-slate-800 border-slate-700 text-white"/>
          </div>
          <div>
            <Label className="text-slate-300">Difficulty</Label>
            <Select value={gen.difficulty} onValueChange={v=>setGen({...gen, difficulty:v})}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue/></SelectTrigger>
              <SelectContent>{["easy","medium","hard","mixed"].map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={generate} disabled={loading} className="mt-4 bg-[#F59E0B] text-[#0A2540] hover:bg-[#F59E0B]/90 font-semibold" data-testid="aiq-generate">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Generating…</> : <><Wand2 className="w-4 h-4 mr-2"/>Generate Questions</>}
        </Button>
      </Card>

      {selected && (
        <Card className="mt-6 p-6 bg-slate-900 border-slate-800">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-white">{selected.job_title}</h3>
              <p className="text-xs text-slate-400">{selected.questions.length} questions · Status: {selected.status}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="border-slate-700 text-slate-200" onClick={saveDraft} data-testid="aiq-save-draft">Save Draft</Button>
              {selected.status !== "approved" && <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={approve} data-testid="aiq-approve"><Check className="w-4 h-4 mr-2"/>Approve & Save</Button>}
            </div>
          </div>
          <div className="space-y-4">
            {selected.questions.map((q, i) => (
              <div key={q.temp_id||i} className="border border-slate-800 rounded-lg p-4 bg-slate-950/40" data-testid={`aiq-q-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex gap-2"><Badge className="bg-slate-700 border-none">{q.skill}</Badge><Badge className="bg-slate-800 border-slate-700 text-slate-300">{q.difficulty}</Badge></div>
                  {selected.status !== "approved" && <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={()=>removeQ(i)}><X className="w-4 h-4"/></Button>}
                </div>
                <Textarea value={q.question} onChange={e=>updateQ(i,"question",e.target.value)} rows={2} className="bg-slate-900 border-slate-700 text-white"/>
                <div className="grid md:grid-cols-2 gap-2 mt-3">
                  {(q.options||[]).map((opt, oi) => (
                    <label key={oi} className={`flex items-center gap-2 p-2 rounded border ${q.correct_index === oi ? "border-emerald-500/60 bg-emerald-500/5" : "border-slate-800 bg-slate-900"}`}>
                      <input type="radio" name={`q${i}`} checked={q.correct_index === oi} onChange={()=>updateQ(i,"correct_index",oi)}/>
                      <Input value={opt} onChange={e=>updateOpt(i,oi,e.target.value)} className="bg-transparent border-0 focus:ring-0 text-white flex-1"/>
                    </label>
                  ))}
                </div>
                <div className="mt-2">
                  <Label className="text-xs text-slate-500">Explanation</Label>
                  <Textarea value={q.explanation||""} onChange={e=>updateQ(i,"explanation",e.target.value)} rows={1} className="bg-slate-900 border-slate-700 text-slate-300 text-xs"/>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="mt-6 p-6 bg-slate-900 border-slate-800">
        <h3 className="text-lg font-semibold text-white mb-4">Generation History</h3>
        {drafts.length === 0 ? <p className="text-sm text-slate-400">No drafts yet.</p> : (
          <div className="space-y-2">
            {drafts.map(d => (
              <div key={d.id} className="flex items-center justify-between p-3 border border-slate-800 rounded-md hover:bg-slate-800/40 cursor-pointer" onClick={()=>openDraft(d.id)} data-testid={`draft-${d.id}`}>
                <div>
                  <div className="text-white text-sm font-medium">{d.job_title}</div>
                  <div className="text-xs text-slate-400">{d.count_generated} questions · {new Date(d.created_at).toLocaleString()}</div>
                </div>
                <Badge className={d.status === "approved" ? "bg-emerald-900/40 text-emerald-300" : "bg-amber-900/40 text-amber-300"}>{d.status}</Badge>
              </div>
            ))}
          </div>
        )}
      </Card>
    </AdminLayout>
  );
}
